/**
 * Timer Store — Estado global del temporizador con Zustand.
 * 
 * Controla el timer de registro de tiempo por cliente.
 * Persiste en localStorage para no perder tiempo en recarga.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TimerState } from '@/types';
import { api } from '@/lib/api';

interface TimerStore extends TimerState {
  start: (clientId: string, clientName: string, taskId?: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<void>;
  tick: () => void;
  setDescription: (desc: string) => void;
  reset: () => void;
}

export const useTimerStore = create<TimerStore>()(
  persist(
    (set, get) => ({
      isRunning: false,
      clientId: null,
      clientName: null,
      taskId: null,
      startTime: null,
      elapsed: 0,
      description: '',

      start: (clientId, clientName, taskId) => {
        set({
          isRunning: true,
          clientId,
          clientName,
          taskId: taskId || null,
          startTime: Date.now(),
          elapsed: 0,
          description: '',
        });
      },

      pause: () => {
        const state = get();
        if (state.startTime) {
          const additionalSeconds = Math.floor(
            (Date.now() - state.startTime) / 1000
          );
          set({
            isRunning: false,
            elapsed: state.elapsed + additionalSeconds,
            startTime: null,
          });
        }
      },

      resume: () => {
        set({
          isRunning: true,
          startTime: Date.now(),
        });
      },

      stop: async () => {
        const state = get();
        let totalSeconds = state.elapsed;
        if (state.startTime) {
          totalSeconds += Math.floor((Date.now() - state.startTime) / 1000);
        }

        if (state.clientId && totalSeconds > 0) {
          try {
            await api.createTimeEntry(state.clientId, {
              task_id: state.taskId,
              description: state.description || 'Tiempo registrado',
              start_time: new Date(
                Date.now() - totalSeconds * 1000
              ).toISOString(),
              end_time: new Date().toISOString(),
              duration_seconds: totalSeconds,
            });
          } catch (error) {
            console.error('Error guardando time entry:', error);
          }
        }

        set({
          isRunning: false,
          clientId: null,
          clientName: null,
          taskId: null,
          startTime: null,
          elapsed: 0,
          description: '',
        });
      },

      tick: () => {
        const state = get();
        if (state.isRunning && state.startTime) {
          // No need to set elapsed here; we compute total on render
          // This just forces a re-render
          set({});
        }
      },

      setDescription: (description) => set({ description }),

      reset: () =>
        set({
          isRunning: false,
          clientId: null,
          clientName: null,
          taskId: null,
          startTime: null,
          elapsed: 0,
          description: '',
        }),
    }),
    {
      name: 'teresita-timer',
    }
  )
);

/** Calcula el tiempo total transcurrido en segundos. */
export function getTotalElapsed(state: TimerState): number {
  let total = state.elapsed;
  if (state.isRunning && state.startTime) {
    total += Math.floor((Date.now() - state.startTime) / 1000);
  }
  return total;
}
